<template>
  <div class="maintenance">
    <div class="maintenance-contents">
      <h2>Congestion Reduction Effort</h2>
      <p>
        At Electrify America, we’re committed to delivering a positive charging experience for every driver. As we
        explore strategies to <strong>reduce wait times and increase throughput</strong> across our network, we’ve
        enforced a State of Charge (SOC) limit at select locations.
      </p>
      <p>
        Once a vehicle reaches <strong>85% State of Charge</strong>, the charging session will automatically end, and
        drivers will have 10 minutes to move their vehicle before idle fees are incurred.
      </p>
      <p>
        Inspired by our customers' feedback, this effort will be conducted across various stations. Our team will
        continuously monitor station performance and customer sentiment and adjust as needed.
      </p>
      <h3>Identifying Stations</h3>
    </div>
    <div class="maintenance-contents">
      <p>
        Check the Electrify America
        <router-link class="link" :to="{ name: 'mobile-app-en' }">mobile app</router-link> or
        <router-link class="link" :to="{ name: 'locate-charger-en' }">Locate a Charger map</router-link> to explore
        nearby stations included in the Congestion Reduction Effort.
      </p>
      <div class="tip">
        <img loading="lazy" src="../assets/images/WhatToExpect/icon-star.png" alt="Charging Limit Tip" />
        <p>
          <strong
            >Stations included in the effort will appear with the “85% Limit” banner or pop-up shown below.</strong
          >
        </p>
      </div>
      <img
        v-if="!isMobile"
        class="app-limit-image"
        loading="lazy"
        alt="Locate a Charger page with the state of charge banner"
        src="@/assets/images/SOCEffort/banners-desktop.jpg"
      />
      <img
        v-else
        class="app-limit-image"
        loading="lazy"
        alt="Locate a Charger page with the state of charge banner"
        src="@/assets/images/SOCEffort/banners-mobile.jpg"
      />
      <br />
      <h3>Frequently Asked Questions (FAQs)</h3>
      <article class="faqs">
        <accordion header-id="soc-faq-heading">
          <accordion-item title="About the Congestion Reduction Effort">
            <accordion header-id="soc-faq-heading">
              <accordion-item title="What is State of Charge (SOC)?">
                <p>
                  State of charge, or SOC, indicates how “full” your EV’s battery is. When charging begins, most EVs
                  send the charger an initial SOC, which is displayed on the charger screen. If available, the SOC will
                  be updated as your car charges, though some EVs are not capable of sending our chargers this
                  information.
                </p>
                <p>
                  Our chargers will also display the time it takes to reach 80% SOC, if your car transmits that
                  information. EVs slow their charging rate as the battery approaches a full charge, which typically
                  occurs around 80%. The SOC that this occurs at is often referred to as “bulk charge.” Vehicles will
                  continue charging to 100% at this slower rate to preserve your battery life.
                </p>
              </accordion-item>
              <accordion-item title="Why is there a limit on how much my EV can charge at this location?">
                <p>
                  This station is part of the congestion reduction effort and has been identified as a high demand
                  station. To reduce wait times and ensure EV drivers can charge in a timely manner, the chargers at
                  this station will end your session once your vehicle reaches 85% State of Charge (SOC).
                </p>
                <p>
                  For more information on charging speeds, please see the section “What happens when I charge” on the
                  <router-link :to="{ name: 'getting-started-en' }" class="link"
                    >Getting Started with Electrify America</router-link
                  >
                  webpage.
                </p>
              </accordion-item>
              <accordion-item title="How is this going to help with queuing?">
                <p>
                  The goal of the congestion reduction effort is to reduce queuing at select high demand charging
                  stations – thereby allowing more people to charge in a timely manner.
                </p>
              </accordion-item>
              <accordion-item title="How long will this effort last?">
                <p>
                  The goal of this congestion reduction effort is to reduce queuing and evaluate customer feedback. We
                  will be monitoring the network and making ongoing changes and adjustments, as necessary.
                </p>
              </accordion-item>
              <accordion-item title="How much can I expect this new system to improve my wait times? ">
                <p>Electrify America anticipates that this will help reduce wait times for EV drivers.</p>
              </accordion-item>
              <accordion-item title="How else is Electrify America going to reduce congestion?">
                <p>
                  Electrify America will continue to build more and larger charging stations to better serve the EV
                  community.
                </p>
              </accordion-item>
            </accordion>
          </accordion-item>
          <accordion-item title="Locations of the Congestion Reduction Effort">
            <accordion header-id="soc-faq-heading">
              <accordion-item title="How do I find which stations are included in the Congestion Reduction Effort?">
                <p>
                  Please check our
                  <router-link class="link" :to="{ name: 'locate-charger-en' }">Locate a Charger map</router-link>, or
                  the <router-link class="link" :to="{ name: 'mobile-app-en' }">mobile app</router-link>, to see if a
                  specific station is part of the congestion reduction effort.
                </p>
              </accordion-item>
              <accordion-item title="How are you choosing the charging stations that have this limitation?">
                <p>
                  The charging stations in the congestion reduction effort were chosen due to high demand and queuing
                  concerns. The locations are in metro areas, not along highway corridors, and we review the distance
                  between stations when selecting locations.
                </p>
              </accordion-item>
            </accordion>
          </accordion-item>
          <accordion-item title="Charging Process with the Congestion Reduction Effort">
            <accordion>
              <accordion-item title="Where can I find my vehicle’s SOC information? ">
                <p>
                  Each electric vehicle (EV) manufacturer is different. It can often be found on your vehicle's dash or
                  instrument cluster, but please see your vehicle manufacturer’s owner’s manual or automotive website
                  for details.
                </p>
              </accordion-item>
              <accordion-item title="What happens when my vehicle hits 85% SOC?">
                <p>
                  Once your EV has reached 85% State of Charge, the charger will stop charging your vehicle, and the
                  10-minute grace period will begin. After the grace period is over, idle fees will be incurred.
                </p>
              </accordion-item>
            </accordion>
          </accordion-item>
          <accordion-item title="Impact of the Congestion Reduction Effort">
            <accordion>
              <accordion-item title="What if I arrive at a charging station with a SOC at or over 85%?">
                <p>The chargers will not start a session if an EV’s State of Charge is at or above 85%.</p>
              </accordion-item>
              <accordion-item title="What happens if I need to charge past that to make it to my next location?">
                <p>
                  Please visit the
                  <router-link class="link" :to="{ name: 'locate-charger-en' }">Locate a Charger map</router-link> or
                  <router-link class="link" :to="{ name: 'mobile-app-en' }">mobile app</router-link> to find another
                  charging station near you.
                </p>
              </accordion-item>
              <accordion-item
                title="Why can’t I charge to 100% if the chargers are all empty and no one is waiting in line?"
              >
                <p>
                  To reduce congestion at the select locations, all charging sessions and EVs will be regulated to 85%
                  State of Charge. Due to fluctuating demands, this change will be in effect at select locations to
                  allow customers to charge in a quick and efficient manner.
                </p>
              </accordion-item>
              <accordion-item title="Can’t I just re-plug in and charge to 100%?">
                <p>
                  Chargers in the congestion reduction effort are programmed to stop charging when your EV has reached
                  85% State of Charge. They are programmed not to begin a charge if it exceeds the SOC limit
                  established. If you unplug and plug back in, the charger will communicate with your vehicle, register
                  the vehicle's state of charge, and will not begin to charge.
                </p>
              </accordion-item>
              <accordion-item title="How will the charger know when my EV has hit 85% SOC?">
                <p>
                  When you plug your EV into an Electrify America charger, the EV and the charger communicate – the
                  vehicle tells the charger the current battery State of Charge, the battery temperature, and other
                  information necessary to start a charging session. After that information is passed over from the
                  vehicle, the Electrify America charger will start the charge if the SOC is below 85%.
                </p>
              </accordion-item>
              <accordion-item title="How will limiting SOC to 85% impact my electric vehicle’s range?">
                <p>
                  The range of each EV is different and depends on many variables such as the weather, the EV battery
                  health, the terrain, etc.
                </p>
                <p>
                  For further questions regarding your EV’s range, please see your vehicle manufacturer’s owner’s manual
                  or automotive website.
                </p>
              </accordion-item>
              <accordion-item title="How will limiting SOC to 85% impact my electric vehicle’s battery health?">
                <p>
                  Toward the end of any charging session, you may notice charging speeds (kW) slow down. EVs “bulk
                  charge” and slow their charging speed when they reach about 80%. Because of this, we recommend
                  unplugging your vehicle after bulk charging has occurred.
                </p>
              </accordion-item>
            </accordion>
          </accordion-item>
        </accordion>
      </article>
    </div>
  </div>
</template>

<script>
import Accordion from '../components/Accordion/Accordion.vue';
import AccordionItem from '../components/Accordion/AccordionItem.vue';

export default {
  name: 'soc-pilot',
  components: {
    Accordion,
    AccordionItem,
  },
  metaInfo: {
    title: 'Congestion Reduction Effort | Electrify America',
    link: [{ rel: 'canonical', href: 'https://www.electrifyamerica.com/soc-pilot/' }],
  },
  computed: {
    isMobile() {
      return this.$resize && this.$mq.below(1130);
    },
  },
};
</script>

<style scoped lang="scss">
.maintenance {
  align-items: center;
  display: flex;
  flex-direction: column;
  padding-top: 50px;
  width: 100%;
  .maintenance-contents {
    max-width: 850px;
    padding-left: 20px;
    padding-right: 20px;

    .faq-header {
      font-weight: 700;
    }
    h3 {
      font-size: 1.8rem;
      padding-top: 20px;
      padding-bottom: 20px;
    }
  }
  .hero-container {
    padding: 20px;
    img {
      width: 100%;
      border-radius: 8px;
    }
  }
}
.app-limit-image {
  max-width: 100%;
  display: block;
  margin-left: auto;
  margin-right: auto;
}
.banner-container {
  display: flex;
  img {
    width: 350px;
  }
  .img-description {
    font-size: 12px;
    padding: 15px;
    display: block;
    margin-left: auto;
    margin-right: auto;
  }
  @media (max-width: 720px) {
    .img-description {
      width: 80%;
    }
    flex-direction: column;
    gap: 25px;
    img {
      width: 80%;
    }
  }
}
article::v-deep {
  .accordion-content {
    margin-left: 30px;
  }
}

.tip {
  display: flex;
  flex-direction: row;
  margin-top: 20px;
  img {
    width: 30px !important;
    height: 30px;
    margin: 0;
  }
  p {
    margin: 0;
    margin-left: 15px;
  }
}
</style>
